import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/ui/page-header"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CampaignStatusBadge } from "@/components/ui/status-badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import {
  ChevronLeft,
  AlertCircle,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  CalendarOff,
  CalendarCheck,
  Pause,
  Play,
  MoreVertical,
  Phone,
  UserPlus,
  MessageSquare,
  Voicemail,
  RefreshCw,
  Calendar,
  Target,
  Globe,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { PostponeDialog } from "@/components/campaigns/PostponeDialog"
import { useToast } from "@/hooks/use-toast"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { formatDateOnly, formatTimestamp } from "@/lib/datetime"
import {
  CampaignFunnel,
  ReminderArms,
  ChannelPauseControl,
  ReliabilityStrip,
  ReplyInbox,
  declineMarker,
  type EnrollmentContext,
  type PausedChannels,
} from "@/features/outreach"
import { cn } from "@/lib/utils"
import type { CampaignEnrollmentResponse } from "@/lib/types"

type TabValue = "overview" | "enrollments" | "inbox"

/** A row of GET /campaigns/{id}/enrollments, plus the patient fetched for display. */
interface Enrollment extends Omit<CampaignEnrollmentResponse, "context" | "paused_channels"> {
  /** last_decline / decline_count: why the campaign is not calling this patient. */
  context: EnrollmentContext
  paused_channels: PausedChannels
  patient?: {
    id?: string
    first_name?: string
    last_name?: string
    phone?: string
    name?: string
  }
  patient_name?: string
  first_name?: string
  last_name?: string
  patient_phone?: string
  phone?: string
}

interface CampaignConfig {
  sms?: {
    enabled?: boolean
    schedule?: string[]
  }
  retry?: {
    retry_delay_minutes?: number
    retry_on_incomplete?: boolean
    retry_on_unavailable?: boolean
  }
  limits?: {
    max_attempts_total?: number
    max_attempts_per_day?: number
  }
  timing?: {
    timezone?: string
    is_recurring?: boolean
    start_date?: string
    end_date?: string
  }
  voicemail?: {
    leave_voicemail?: boolean
  }
  completion?: {
    criteria?: string
  }
  call_windows?: {
    daily_windows?: { start: string; end: string }[]
    allowed_days_of_week?: number[]
  }
  purpose?: string
  cadence?: {
    retry_spacing_days?: number
    max_attempts?: number
    first_call?: string
    text_before_each_call?: boolean
    intro_text?: boolean
  }
}

export default function CampaignDetailPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { id: campaignId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedOrganizationId } = useOrganizationsStore()

  const [campaign, setCampaign] = useState<any | null>(null)
  const [stats, setStats] = useState<{
    enrolled_count: number
    pending_count: number
    completed_count: number
    failed_count: number
  } | null>(null)
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false)
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null)
  const [campaignPostponeDialogOpen, setCampaignPostponeDialogOpen] = useState(false)

  const activeTab = (searchParams.get("tab") as TabValue) || "overview"
  const [outreachRefresh, setOutreachRefresh] = useState(0)
  const [inboxCount, setInboxCount] = useState<number | null>(null)

  const setActiveTab = (tab: TabValue) => {
    setSearchParams({ tab })
  }

  const fetchCampaignData = async () => {
    if (!campaignId || !selectedOrganizationId) return

    setLoading(true)
    setError(null)

    try {
      const [campaignData, enrollmentsData, statsData] = await Promise.all([
        api.getCampaign(selectedOrganizationId, campaignId),
        api.getCampaignEnrollments(selectedOrganizationId, campaignId),
        api.getCampaignStats(selectedOrganizationId, campaignId).catch(() => null),
      ])

      setCampaign(campaignData)

      // Fetch patient details for each enrollment
      const enrollmentsWithPatients = await Promise.all(
        (enrollmentsData || []).map(async (row): Promise<Enrollment> => {
          const enrollment: Enrollment = {
            ...row,
            context: (row.context || {}) as EnrollmentContext,
            paused_channels: row.paused_channels ?? null,
          }
          if (!enrollment.patient_id) return enrollment
          try {
            const patient = await api.getPatient(enrollment.patient_id)
            return {
              ...enrollment,
              patient: {
                id: patient.id,
                first_name: patient.first_name ?? undefined,
                last_name: patient.last_name ?? undefined,
                phone: patient.phone_number ?? undefined,
              },
            }
          } catch (err) {
            console.error(`Failed to fetch patient ${enrollment.patient_id}:`, err)
            return enrollment
          }
        })
      )

      setEnrollments(enrollmentsWithPatients)

      // Use stats from API or calculate from enrollments
      if (statsData) {
        // Map API response to our stats format
        const byStatus = statsData.by_status || {}
        setStats({
          enrolled_count: statsData.total_enrollments || 0,
          pending_count: (byStatus.ACTIVE || 0) + (byStatus.PAUSED || 0),
          completed_count: byStatus.COMPLETED || 0,
          failed_count: (byStatus.FAILED || 0) + (byStatus.CANCELLED || 0),
        })
      } else {
        // Calculate stats from enrollments
        const enrolled = enrollmentsData?.length || 0
        const completed = enrollmentsData?.filter((e: any) => e.status?.toUpperCase() === "COMPLETED").length || 0
        const failed = enrollmentsData?.filter((e: any) =>
          e.status?.toUpperCase() === "FAILED" || e.status?.toUpperCase() === "CANCELLED"
        ).length || 0
        const pending = enrolled - completed - failed
        setStats({
          enrolled_count: enrolled,
          pending_count: pending,
          completed_count: completed,
          failed_count: failed,
        })
      }
    } catch (err: any) {
      console.error("Error fetching campaign:", err)
      setError(err?.response?.data?.detail || "Failed to load campaign")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCampaignData()
  }, [campaignId, selectedOrganizationId])

  const handlePostponeCampaign = () => {
    setCampaignPostponeDialogOpen(true)
  }

  const handleCampaignPostponeConfirm = async (pauseStart: string, pauseEnd: string, reason?: string) => {
    if (!campaignId) return

    try {
      await api.postponeCampaign(campaignId, {
        pause_start: pauseStart,
        pause_end: pauseEnd,
        reason,
      })
      toast({
        title: "Campaign paused",
        description: `Calls will be paused from ${formatDateOnly(pauseStart, "MMM d")} to ${formatDateOnly(pauseEnd, "MMM d")}`,
      })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to pause campaign:", err)
      toast({ title: "Failed to pause campaign", variant: "destructive" })
      throw err
    }
  }

  const handlePauseCampaignIndefinitely = async () => {
    if (!campaignId) return

    try {
      await api.pauseCampaign(campaignId)
      toast({ title: "Campaign paused" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to pause campaign:", err)
      toast({ title: "Failed to pause campaign", variant: "destructive" })
      throw err
    }
  }

  const handleResumeCampaign = async () => {
    if (!campaignId) return

    try {
      await api.resumeCampaign(campaignId)
      toast({ title: "Campaign resumed" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to resume campaign:", err)
      toast({ title: "Failed to resume campaign", variant: "destructive" })
    }
  }

  const handleClearCampaignPostpone = async () => {
    if (!campaignId) return

    try {
      await api.clearCampaignPostpone(campaignId)
      toast({ title: "Pause dates cleared" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to clear pause dates:", err)
      toast({ title: "Failed to clear pause dates", variant: "destructive" })
    }
  }

  const handleDeleteCampaign = async () => {
    if (!campaignId) return
    if (!window.confirm(`Are you sure you want to delete "${campaign?.name}"? This will archive the campaign.`)) return

    try {
      await api.deleteCampaign(campaignId)
      toast({ title: "Campaign deleted" })
      navigate("/campaigns")
    } catch (err) {
      console.error("Failed to delete campaign:", err)
      toast({ title: "Failed to delete campaign", variant: "destructive" })
    }
  }

  // Enrollment actions
  const handlePostponeEnrollment = (enrollment: Enrollment) => {
    setSelectedEnrollment(enrollment)
    setPostponeDialogOpen(true)
  }

  const handleEnrollmentPostponeConfirm = async (pauseStart: string, pauseEnd: string, reason?: string) => {
    if (!selectedEnrollment || !campaignId) return

    try {
      await api.postponeEnrollment(campaignId, selectedEnrollment.id, {
        pause_start: pauseStart,
        pause_end: pauseEnd,
        reason,
      })
      toast({
        title: "Enrollment paused",
        description: `Calls will be paused from ${formatDateOnly(pauseStart, "MMM d")} to ${formatDateOnly(pauseEnd, "MMM d")}`,
      })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to pause enrollment:", err)
      toast({ title: "Failed to pause enrollment", variant: "destructive" })
      throw err
    }
  }

  const handlePauseEnrollment = async (enrollment: Enrollment) => {
    if (!campaignId) return

    try {
      await api.pauseEnrollment(campaignId, enrollment.id)
      toast({ title: "Enrollment paused" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to pause enrollment:", err)
      toast({ title: "Failed to pause enrollment", variant: "destructive" })
    }
  }

  const handleResumeEnrollment = async (enrollment: Enrollment) => {
    if (!campaignId) return

    try {
      await api.resumeEnrollment(campaignId, enrollment.id)
      toast({ title: "Enrollment resumed" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to resume enrollment:", err)
      toast({ title: "Failed to resume enrollment", variant: "destructive" })
    }
  }

  const handleRemoveEnrollment = async (enrollment: Enrollment) => {
    if (!campaignId) return

    try {
      await api.removeEnrollment(campaignId, enrollment.id)
      toast({ title: "Removed from campaign" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to remove enrollment:", err)
      toast({ title: "Failed to remove enrollment", variant: "destructive" })
    }
  }

  const handleClearEnrollmentPostpone = async (enrollment: Enrollment) => {
    if (!campaignId) return

    try {
      await api.clearEnrollmentPostpone(campaignId, enrollment.id)
      toast({ title: "Pause dates cleared" })
      await fetchCampaignData()
    } catch (err) {
      console.error("Failed to clear pause dates:", err)
      toast({ title: "Failed to clear pause dates", variant: "destructive" })
    }
  }

  const isPostponed = campaign?.pause_start && campaign?.pause_end
  const isPaused = campaign?.status?.toLowerCase() === "paused"
  // WorkflowDefinitionResponse carries org_ids (an org's own campaign has exactly one).
  const orgId: string | null =
    (campaign?.org_ids?.length === 1 ? String(campaign.org_ids[0]) : null) || selectedOrganizationId
  // Consent campaigns run on the outreach engine (texts + calls, reply inbox).
  const isOutreachCampaign =
    (campaign?.config?.purpose || "").toUpperCase() === "CONSENT" || !!campaign?.config?.cadence
  const campaignPausedChannels: PausedChannels =
    campaign?.paused_channels !== undefined ? campaign.paused_channels : isPaused ? "ALL" : null

  // attempts = calls that went out; max from the enrollment list, else the campaign's cadence.
  const attemptsText = (e: Enrollment): string => {
    const max = e.max_attempts ?? campaign?.config?.cadence?.max_attempts
    return max != null ? `${e.attempts} / ${max}` : String(e.attempts)
  }

  // Why the campaign is not calling this patient (its booking keeps being refused).
  const renderDeclineMarker = (e: Enrollment) => {
    const marker = declineMarker(e.context)
    if (!marker) return null
    const when = formatTimestamp(marker.at, "MMM d, h:mm a") || null
    return (
      <div
        className={cn(
          "max-w-xs text-xs break-words",
          marker.stuck
            ? "rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 font-medium text-destructive"
            : "text-warning",
        )}
        title={when ? `Last refused ${when}` : undefined}
      >
        {marker.stuck ? "Stuck, " : ""}
        {marker.text}
        {marker.count > 1 ? ` (${marker.count} times in a row)` : ""}
      </div>
    )
  }

  // Shared between the phone card list and the desktop table so both expose the same actions.
  const renderEnrollmentActions = (enrollment: Enrollment) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => navigate(`/patients/${enrollment.patient_id}`)}>
          View Patient
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {enrollment.status?.toLowerCase() === "paused" ? (
          <DropdownMenuItem onSelect={() => handleResumeEnrollment(enrollment)}>
            <Play className="h-4 w-4 mr-2" />
            Resume
          </DropdownMenuItem>
        ) : enrollment.status?.toLowerCase() === "active" ? (
          <>
            <DropdownMenuItem onSelect={() => handlePauseEnrollment(enrollment)}>
              <Pause className="h-4 w-4 mr-2" />
              Pause Indefinitely
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handlePostponeEnrollment(enrollment)}>
              <CalendarOff className="h-4 w-4 mr-2" />
              Pause with Dates
            </DropdownMenuItem>
          </>
        ) : null}
        {enrollment.pause_start && enrollment.pause_end && (
          <DropdownMenuItem onSelect={() => handleClearEnrollmentPostpone(enrollment)}>
            <CalendarCheck className="h-4 w-4 mr-2" />
            Clear Pause Dates
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive"
          onSelect={() => handleRemoveEnrollment(enrollment)}
        >
          Remove from Campaign
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const getProgressValue = () => {
    const total = stats?.enrolled_count || 0
    const completed = stats?.completed_count || 0
    if (total === 0) return 0
    return Math.round((completed / total) * 100)
  }

  const getEnrollmentStatusBadge = (status: string, pausedChannels?: PausedChannels) => {
    const statusLower = status?.toLowerCase()
    if (pausedChannels === "SMS" || pausedChannels === "CALL") {
      return (
        <Badge className="bg-warning/10 text-warning border-warning/30">
          {pausedChannels === "SMS" ? "Texts paused" : "Calls paused"}
        </Badge>
      )
    }
    switch (statusLower) {
      case "active":
        return <Badge className="bg-success/10 text-success border-success/30">Active</Badge>
      case "paused":
        return <Badge className="bg-warning/10 text-warning border-warning/30">Paused</Badge>
      case "completed":
        return <Badge className="bg-info/10 text-info border-info/30">Completed</Badge>
      case "removed":
      case "failed":
        return <Badge className="bg-destructive/10 text-destructive border-destructive/30">{status}</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getPatientName = (enrollment: Enrollment) => {
    // Check nested patient object first
    if (enrollment.patient?.name) return enrollment.patient.name
    if (enrollment.patient?.first_name || enrollment.patient?.last_name) {
      return `${enrollment.patient.first_name || ""} ${enrollment.patient.last_name || ""}`.trim()
    }
    // Check flat fields
    if (enrollment.patient_name) return enrollment.patient_name
    if (enrollment.first_name || enrollment.last_name) {
      return `${enrollment.first_name || ""} ${enrollment.last_name || ""}`.trim()
    }
    return enrollment.patient_id
  }

  const getPatientPhone = (enrollment: Enrollment) => {
    return enrollment.patient?.phone || enrollment.patient_phone || enrollment.phone
  }

  const config: CampaignConfig = campaign?.config || {}

  const formatDayOfWeek = (day: number) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    return days[day] || day
  }

  const formatCallWindows = () => {
    const windows = config.call_windows?.daily_windows || []
    if (windows.length === 0) return null
    return windows.map((w, i) => `${w.start} - ${w.end}`).join(", ")
  }

  const formatAllowedDays = () => {
    const days = config.call_windows?.allowed_days_of_week || []
    if (days.length === 0) return null
    if (days.length === 7) return "Every day"
    if (days.length === 5 && !days.includes(5) && !days.includes(6)) return "Weekdays"
    return days.map(formatDayOfWeek).join(", ")
  }

  // Loading state
  if (loading && !campaign) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-10 w-64 max-w-full" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-8">
        <Button variant="ghost" className="mb-4" onClick={() => navigate("/campaigns")}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Campaigns
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  // Not found state
  if (!campaign) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-8">
        <Button variant="ghost" className="mb-4" onClick={() => navigate("/campaigns")}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Campaigns
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Not Found</AlertTitle>
          <AlertDescription>Campaign not found</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
      {/* Back button and header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 space-y-1">
          <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/campaigns")}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Campaigns
          </Button>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <h1 className="text-2xl font-semibold break-words">{campaign.name}</h1>
            <CampaignStatusBadge status={campaign.status} />
            {isPostponed && (
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                <CalendarOff className="h-3 w-3 mr-1" />
                Paused {formatTimestamp(campaign.pause_start, "MMM d")} - {formatTimestamp(campaign.pause_end, "MMM d")}
              </Badge>
            )}
          </div>
          {campaign.description && (
            <p className="text-sm text-muted-foreground">{campaign.description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isOutreachCampaign && campaignId && (
            <ChannelPauseControl
              scope="campaign"
              targetId={campaignId}
              subject={`everyone in "${campaign.name}"`}
              pausedChannels={campaignPausedChannels}
              currentReason={campaign.pause_reason}
              size="default"
              onChange={(s) => {
                setCampaign((c: any) => ({
                  ...c,
                  paused_channels: s.paused_channels,
                  pause_reason: s.reason,
                  status: s.paused_channels ? "PAUSED" : "ACTIVE",
                }))
                setOutreachRefresh((n) => n + 1)
              }}
            />
          )}
          {isOutreachCampaign ? null : isPaused ? (
            <Button onClick={handleResumeCampaign} className="flex-1 md:flex-none">
              <Play className="h-4 w-4 mr-2" />
              Resume Campaign
            </Button>
          ) : (
            <Button variant="outline" onClick={handlePostponeCampaign} className="flex-1 md:flex-none">
              <Pause className="h-4 w-4 mr-2" />
              Pause Campaign
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isPostponed && (
                <DropdownMenuItem onSelect={handleClearCampaignPostpone}>
                  <CalendarCheck className="h-4 w-4 mr-2" />
                  Clear Pause Dates
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onSelect={handleDeleteCampaign}>
                Delete Campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* APCM redirect banner */}
      {campaign.name === "APCM" && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Managed via APCM Operations</AlertTitle>
          <AlertDescription>
            This campaign is managed through the APCM Operations dashboard.{" "}
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/apcm-ops")}>
              Go to APCM Ops
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <TooltipProvider>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          <Card className="min-w-0">
            <CardContent className="p-4 md:pt-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-info/10 flex items-center justify-center">
                      <Users className="h-5 w-5 text-info" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-semibold">{stats?.enrolled_count || 0}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">Enrolled</p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Total patients currently enrolled</p>
                </TooltipContent>
              </Tooltip>
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardContent className="p-4 md:pt-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-warning/10 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-warning" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-semibold">{stats?.pending_count || 0}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">In Progress</p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Patients actively being contacted</p>
                </TooltipContent>
              </Tooltip>
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardContent className="p-4 md:pt-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-success/10 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-success" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-semibold">{stats?.completed_count || 0}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Patients who completed the campaign</p>
                </TooltipContent>
              </Tooltip>
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardContent className="p-4 md:pt-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-destructive/10 flex items-center justify-center">
                      <XCircle className="h-5 w-5 text-destructive" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl md:text-2xl font-semibold">{stats?.failed_count || 0}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">Failed</p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Patients who failed or were cancelled</p>
                </TooltipContent>
              </Tooltip>
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>

      {isOutreachCampaign && orgId && (
        <ReliabilityStrip orgId={orgId} onOpenPatient={(pid) => navigate(`/patients/${pid}`)} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
        <TabsList className="w-full justify-start overflow-x-auto md:w-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="enrollments">
            Enrollments ({enrollments.length})
          </TabsTrigger>
          {isOutreachCampaign && (
            <TabsTrigger value="inbox">
              Reply inbox{inboxCount !== null ? ` (${inboxCount})` : ""}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {isOutreachCampaign && orgId && campaignId && (
            <CampaignFunnel orgId={orgId} campaignId={campaignId} refreshKey={outreachRefresh} />
          )}
          {isOutreachCampaign && orgId && campaignId && (
            <ReminderArms orgId={orgId} campaignId={campaignId} refreshKey={outreachRefresh} />
          )}
          <Card>
            <CardHeader>
              <CardTitle>Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completion Rate</span>
                  <span className="font-medium">{getProgressValue()}%</span>
                </div>
                <Progress value={getProgressValue()} className="h-3" />
              </div>

              {campaign.today_scheduled > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Today: {campaign.today_completed || 0} of {campaign.today_scheduled} calls completed
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completion Criteria */}
          {config.completion?.criteria && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="h-4 w-4" />
                  Completion Criteria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{config.completion.criteria}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Campaign Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-4 w-4" />
                  Campaign Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <CampaignStatusBadge status={campaign.status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Created</span>
                  <span className="text-sm">
                    {campaign.created_at
                      ? formatTimestamp(campaign.created_at, "MMM d, yyyy")
                      : "-"}
                  </span>
                </div>
                {config.timing?.timezone && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Timezone</span>
                    <span className="text-sm">{config.timing.timezone}</span>
                  </div>
                )}
                {campaign.strategy_id && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Strategy ID</span>
                    <span className="text-sm font-mono text-xs">{campaign.strategy_id.slice(0, 8)}...</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Call Windows */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Call Windows
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {formatCallWindows() && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-muted-foreground shrink-0">Hours</span>
                    <span className="text-sm text-right break-words">{formatCallWindows()}</span>
                  </div>
                )}
                {formatAllowedDays() && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-muted-foreground shrink-0">Days</span>
                    <span className="text-sm text-right break-words">{formatAllowedDays()}</span>
                  </div>
                )}
                {!formatCallWindows() && !formatAllowedDays() && (
                  <p className="text-sm text-muted-foreground">No call windows configured</p>
                )}
              </CardContent>
            </Card>

            {/* Limits */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Phone className="h-4 w-4" />
                  Call Limits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {config.cadence?.max_attempts ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Max Calls per Patient</span>
                      <span className="text-sm">{config.cadence.max_attempts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Days Between Calls</span>
                      <span className="text-sm">{config.cadence.retry_spacing_days ?? 7}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Texts</span>
                      <span className="text-sm text-right">
                        {[
                          config.cadence.intro_text !== false ? "intro" : null,
                          config.cadence.text_before_each_call !== false ? "reminder before each call" : null,
                        ]
                          .filter(Boolean)
                          .join(", ") || "none"}
                      </span>
                    </div>
                  </>
                ) : config.limits?.max_attempts_total ? (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Max Attempts Total</span>
                    <span className="text-sm">{config.limits.max_attempts_total}</span>
                  </div>
                ) : null}
                {config.limits?.max_attempts_per_day && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Max Per Day</span>
                    <span className="text-sm">{config.limits.max_attempts_per_day}</span>
                  </div>
                )}
                {!config.cadence?.max_attempts && !config.limits?.max_attempts_total && !config.limits?.max_attempts_per_day && (
                  <p className="text-sm text-muted-foreground">No limits configured</p>
                )}
              </CardContent>
            </Card>

            {/* Retry Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <RefreshCw className="h-4 w-4" />
                  Retry Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {config.retry?.retry_delay_minutes && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Retry Delay</span>
                    <span className="text-sm">{config.retry.retry_delay_minutes} min</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Retry on Incomplete</span>
                  <span className="text-sm">{config.retry?.retry_on_incomplete ? "Yes" : "No"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Retry on Unavailable</span>
                  <span className="text-sm">{config.retry?.retry_on_unavailable ? "Yes" : "No"}</span>
                </div>
              </CardContent>
            </Card>

            {/* SMS Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-4 w-4" />
                  SMS Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">SMS Enabled</span>
                  <Badge variant={config.sms?.enabled ? "default" : "outline"}>
                    {config.sms?.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                {config.sms?.enabled && config.sms?.schedule && config.sms.schedule.length > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-muted-foreground shrink-0">Schedule</span>
                    <span className="text-sm text-right break-words">{config.sms.schedule.map(s => s.replace(/_/g, " ")).join(", ")}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Voicemail Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Voicemail className="h-4 w-4" />
                  Voicemail Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Leave Voicemail</span>
                  <Badge variant={config.voicemail?.leave_voicemail ? "default" : "outline"}>
                    {config.voicemail?.leave_voicemail ? "Yes" : "No"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="enrollments" className="mt-4">
          <Card className="min-w-0">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>Enrolled Patients</CardTitle>
              <Button variant="outline" size="sm" className="shrink-0">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Patients
              </Button>
            </CardHeader>
            <CardContent className="px-3 md:px-6">
              {enrollments.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="font-medium mb-1">No enrollments yet</h3>
                  <p className="text-sm text-muted-foreground">Add patients to this campaign to get started</p>
                </div>
              ) : (
                <>
                {/* Phone: card list */}
                <div className="md:hidden divide-y -mx-3 border-t">
                  {enrollments.map((enrollment) => (
                    <div key={enrollment.id} className="flex items-start gap-2 px-3 py-3">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <button
                              className="font-medium hover:underline text-left break-words"
                              onClick={() => navigate(`/patients/${enrollment.patient_id}`)}
                            >
                              {getPatientName(enrollment)}
                            </button>
                            {getPatientPhone(enrollment) && (
                              <p className="text-sm text-muted-foreground truncate">{getPatientPhone(enrollment)}</p>
                            )}
                          </div>
                          <div className="shrink-0">{getEnrollmentStatusBadge(enrollment.status, enrollment.paused_channels)}</div>
                        </div>
                        {enrollment.pause_start && enrollment.pause_end && (
                          <div className="text-xs text-warning">
                            Paused {formatTimestamp(enrollment.pause_start, "MMM d")} - {formatTimestamp(enrollment.pause_end, "MMM d")}
                          </div>
                        )}
                        {renderDeclineMarker(enrollment)}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>Attempts {attemptsText(enrollment)}</span>
                          <span>
                            Next{" "}
                            {enrollment.next_call_date
                              ? formatTimestamp(enrollment.next_call_date, "MMM d, h:mm a")
                              : "-"}
                          </span>
                          <span>
                            Enrolled{" "}
                            {(enrollment.enrolled_at || enrollment.created_at)
                              ? formatTimestamp(enrollment.enrolled_at || enrollment.created_at, "MMM d, yyyy")
                              : "-"}
                          </span>
                        </div>
                      </div>
                      <div className="shrink-0 -mr-2">
                        {renderEnrollmentActions(enrollment)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tablet / desktop: table */}
                <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Next Call</TableHead>
                      <TableHead>Enrolled</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.map((enrollment) => (
                      <TableRow key={enrollment.id}>
                        <TableCell>
                          <button
                            className="font-medium hover:underline text-left"
                            onClick={() => navigate(`/patients/${enrollment.patient_id}`)}
                          >
                            {getPatientName(enrollment)}
                          </button>
                          {getPatientPhone(enrollment) && (
                            <p className="text-sm text-muted-foreground">{getPatientPhone(enrollment)}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getEnrollmentStatusBadge(enrollment.status, enrollment.paused_channels)}
                            {enrollment.pause_start && enrollment.pause_end && (
                              <span className="text-xs text-warning">
                                Paused {formatTimestamp(enrollment.pause_start, "MMM d")} - {formatTimestamp(enrollment.pause_end, "MMM d")}
                              </span>
                            )}
                            {renderDeclineMarker(enrollment)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {attemptsText(enrollment)}
                        </TableCell>
                        <TableCell>
                          {enrollment.next_call_date
                            ? formatTimestamp(enrollment.next_call_date, "MMM d, h:mm a")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {(enrollment.enrolled_at || enrollment.created_at)
                            ? formatTimestamp(enrollment.enrolled_at || enrollment.created_at, "MMM d, yyyy")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {renderEnrollmentActions(enrollment)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {isOutreachCampaign && orgId && (
          <TabsContent value="inbox" className="mt-4">
            <ReplyInbox orgId={orgId} onCountChange={setInboxCount} />
          </TabsContent>
        )}
      </Tabs>

      {/* Campaign Postpone Dialog */}
      <PostponeDialog
        open={campaignPostponeDialogOpen}
        onOpenChange={setCampaignPostponeDialogOpen}
        title="Pause Campaign"
        description="Choose how you want to pause this campaign."
        onConfirm={handleCampaignPostponeConfirm}
        onPauseIndefinitely={handlePauseCampaignIndefinitely}
        showIndefiniteOption={true}
        currentPauseStart={campaign?.pause_start}
        currentPauseEnd={campaign?.pause_end}
        isPaused={isPaused}
      />

      {/* Enrollment Postpone Dialog */}
      <PostponeDialog
        open={postponeDialogOpen}
        onOpenChange={setPostponeDialogOpen}
        title="Pause Enrollment"
        description="Choose the dates to pause this patient's enrollment."
        onConfirm={handleEnrollmentPostponeConfirm}
        currentPauseStart={selectedEnrollment?.pause_start}
        currentPauseEnd={selectedEnrollment?.pause_end}
      />
    </div>
  )
}
